/**
 * RYOXZEL product page progressive enhancement (P01–P10 hero / buy box).
 *
 * Small controllers, all scoped to the PDP hero section and all safe to run
 * repeatedly (Shopify Theme Editor reloads sections without a full page load):
 *
 * 1. Accordion motion — Horizon already renders native <details> and animates
 *    height, opacity and the plus/minus icon in CSS. This controller only adds
 *    the reference's dynamic duration (shorter travel = shorter motion) and an
 *    opt-in single-open group for the product detail accordion. Native
 *    semantics, keyboard access, focus-visible and the reduced-motion fallback
 *    all stay with Horizon.
 *
 * 2. Pair With / Upgrade tabs — semantic tablist behaviour (roles, roving
 *    tabindex, arrow/Home/End keys) for the RYOXZEL upsell block. Commerce
 *    inside the panels stays with Horizon (product recommendations, quick add).
 *
 * 3. Mobile media gallery — `@ryoxzel/gallery-loop` gives the rail its
 *    seamless loop and the RYOXZEL arrow controls; Horizon keeps the slide
 *    index, snapping and variant sync. The quick-add card uses the same
 *    module, which is what keeps the two carousels one behaviour.
 *    variant sync.
 *
 * 4. Option helper — a second line inside Horizon's own option labels, so a
 *    bare weight also says what it means. `blocks/ryoxzel-pdp-option-helper`
 *    supplies merchant-authored text; Horizon keeps the group, the radios,
 *    availability and the variant events.
 *
 * No commerce state is read or written here.
 */

import { enhanceGalleryLoop } from '@ryoxzel/gallery-loop';
import { initQuantityTiers, teardownQuantityTiers } from '@ryoxzel/quantity-tiers';

const PDP_SCOPE = '.product-information';
// Any accordion inside the buy box behaves as the product detail accordion
// (single-open group). Other placements opt in explicitly with the data
// attribute, so no other accordion on the site changes behaviour.
const ACCORDION_SELECTOR = `${PDP_SCOPE} [data-ryx-pdp-accordion], ${PDP_SCOPE} .product-details .accordion`;
const PDP_PRODUCT_DETAILS = `${PDP_SCOPE} .product-details`;
const PDP_FAQ_SELECTOR = `${PDP_SCOPE} .ryoxzel-pdp-faq`;
const PDP_CHECKLIST_SELECTOR = `${PDP_SCOPE} .ryoxzel-pdp-checklist`;
const TABS_SELECTOR = `${PDP_SCOPE} [data-ryx-pdp-tabs]`;
const OPTION_HELPER_SELECTOR = `${PDP_SCOPE} [data-ryx-pdp-option-helper]`;
const OPTION_GROUP_SELECTOR = `${PDP_SCOPE} .variant-picker fieldset.variant-option`;
const OPTION_PICKER_SELECTOR = `${PDP_SCOPE} .variant-picker`;
const OPTION_LABEL_SELECTOR = '.variant-option__button-label';
const OPTION_LABEL_TEXT_SELECTOR = '.variant-option__button-label__text';
const HELPER_LINE_CLASS = 'ryoxzel-pdp-option-helper__line';
const HELPER_FLAG_CLASS = 'ryoxzel-pdp-option-helper__flag';

const MIN_ACCORDION_MS = 240;
const MAX_ACCORDION_MS = 480;
const ACCORDION_PX_PER_MS = 1.6;

// Disclosure bodies often wrap to more lines on mobile. Keep the approved
// desktop feel as the floor, then lengthen only as the actual content travel
// requires. Both RYOXZEL PDP disclosures — the FAQ card and the description
// card — measure against this one curve, so they cannot drift apart.
const DISCLOSURE_MIN_ACCORDION_MS = 280;
const DISCLOSURE_MAX_ACCORDION_MS = 480;
const DISCLOSURE_ACCORDION_PX_PER_MS = 0.22;

const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const GALLERY_SELECTOR = `${PDP_SCOPE} media-gallery[data-presentation='carousel']`;
const enhancedGalleries = new Set();

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const accordionDuration = (distance) =>
  `${Math.min(
    MAX_ACCORDION_MS,
    Math.max(MIN_ACCORDION_MS, Math.round(Math.abs(distance) / ACCORDION_PX_PER_MS))
  )}ms`;

const disclosureDuration = (distance) =>
  `${Math.min(
    DISCLOSURE_MAX_ACCORDION_MS,
    Math.max(DISCLOSURE_MIN_ACCORDION_MS, Math.round(Math.abs(distance) / DISCLOSURE_ACCORDION_PX_PER_MS))
  )}ms`;

/* -------------------------------------------------------------------------
 * 1. Accordion motion
 * ---------------------------------------------------------------------- */

function enhanceAccordion(host) {
  if (!(host instanceof HTMLElement) || host.dataset.ryxPdpAccordionReady === 'true') return;
  host.dataset.ryxPdpAccordionReady = 'true';

  const details = Array.from(host.querySelectorAll('details'));
  if (details.length === 0) return;

  const singleOpen = host.dataset.ryxPdpAccordion
    ? host.dataset.ryxPdpAccordion === 'single'
    : Boolean(host.closest(PDP_PRODUCT_DETAILS));

  const applyDuration = (node) => {
    const body = node.querySelector('.details-content');
    if (!body) return;
    // Only measure while collapsed: an open row already has its final height.
    const distance = node.open ? body.getBoundingClientRect().height : body.scrollHeight;
    if (!distance) return;
    node.style.setProperty('--ryx-pdp-accordion-duration', accordionDuration(distance));
  };

  for (const node of details) {
    applyDuration(node);

    node.addEventListener(
      'toggle',
      () => {
        applyDuration(node);

        if (!singleOpen || !node.open) return;

        for (const other of details) {
          if (other === node || !other.open) continue;
          other.open = false;
        }
      },
      { passive: true }
    );
  }
}

function initAccordions(root = document) {
  for (const host of root.querySelectorAll(ACCORDION_SELECTOR)) enhanceAccordion(host);
}

/* -------------------------------------------------------------------------
 * 1b. Disclosure motion — the FAQ card and the description card
 *
 * One controller for both. They share the measured duration curve, the toggle
 * listener and the re-measure on a width change. Only the CSS hook differs,
 * because each component's stylesheet owns its own property; the behaviour
 * itself is one implementation, so the two cannot drift apart.
 * ---------------------------------------------------------------------- */

const DISCLOSURES = [
  {
    selector: PDP_FAQ_SELECTOR,
    item: '.ryoxzel-pdp-faq__item',
    body: '.ryoxzel-pdp-faq__answer',
    durationVar: '--ryx-pdp-faq-accordion-duration',
    ready: 'ryxPdpFaqAccordionReady',
    teardown: '_ryxPdpFaqAccordionTeardown',
  },
  {
    selector: PDP_CHECKLIST_SELECTOR,
    item: '.ryoxzel-pdp-checklist__disclosure',
    body: '.ryoxzel-pdp-checklist__body',
    durationVar: '--ryx-pdp-checklist-accordion-duration',
    ready: 'ryxPdpChecklistAccordionReady',
    teardown: '_ryxPdpChecklistAccordionTeardown',
  },
];

function enhanceDisclosure(host, config) {
  if (!(host instanceof HTMLElement) || host.dataset[config.ready] === 'true') return;
  host.dataset[config.ready] = 'true';

  const details = Array.from(host.querySelectorAll(config.item));
  if (details.length === 0) return;

  const applyDuration = (node) => {
    const body = node.querySelector(config.body);
    if (!body) return;

    const distance = node.open ? body.getBoundingClientRect().height : body.scrollHeight;
    if (!distance) return;
    node.style.setProperty(config.durationVar, disclosureDuration(distance));
  };

  const refreshDurations = () => details.forEach(applyDuration);
  refreshDurations();

  for (const node of details) {
    node.addEventListener('toggle', () => applyDuration(node), { passive: true });
  }

  // Re-measure after a responsive width change, before the next user toggle.
  // This keeps the perceived speed independent of how many lines the body
  // occupies at desktop or mobile widths.
  const observer = new ResizeObserver(refreshDurations);
  observer.observe(host);
  host[config.teardown] = () => observer.disconnect();
}

function initDisclosures(root = document) {
  for (const config of DISCLOSURES) {
    for (const host of root.querySelectorAll(config.selector)) enhanceDisclosure(host, config);
  }
}

function teardownDisclosures(root) {
  for (const config of DISCLOSURES) {
    const hosts = [];
    if (root instanceof HTMLElement && root.matches(config.selector)) hosts.push(root);
    if (root instanceof Element || root instanceof Document) {
      hosts.push(...root.querySelectorAll(config.selector));
    }

    for (const host of hosts) {
      host[config.teardown]?.();
      delete host[config.teardown];
      delete host.dataset[config.ready];
    }
  }
}

/* -------------------------------------------------------------------------
 * 2. Pair With / Upgrade tabs
 * ---------------------------------------------------------------------- */

function enhanceTabs(host) {
  if (!(host instanceof HTMLElement) || host.dataset.ryxPdpTabsReady === 'true') return;

  const panelsRoot = host.querySelector('[data-ryx-pdp-panels]');
  const panels = panelsRoot
    ? Array.from(panelsRoot.children).filter((child) => child.nodeType === Node.ELEMENT_NODE)
    : [];

  if (panels.length === 0) return;

  host.dataset.ryxPdpTabsReady = 'true';

  const tabs = Array.from(host.querySelectorAll('[data-ryx-pdp-tab]'));
  const nav = host.querySelector('.ryoxzel-pdp-tabs__nav');
  const singleLabel = host.querySelector('.ryoxzel-pdp-tabs__label');
  const canWireTabs = tabs.length >= 2 && tabs.length === panels.length;

  /**
   * A panel only earns a tab when it actually renders something. Horizon's
   * recommendation panel reports its own empty state, and a block that has no
   * data (for example the upgrade panel without its metafield) marks itself with
   * `data-ryx-pdp-empty="true"`.
   */
  const panelHasContent = (panel) => {
    if (!panel) return false;
    if (panel.querySelector('[data-ryx-pdp-empty="true"]')) return false;
    if (panel.querySelector('[data-has-recommendations="false"]')) return false;
    const media = panel.querySelector('img, svg, video, iframe, model-viewer, .swatch');
    const items = panel.querySelectorAll(
      '.resource-list__item, product-card, .ryoxzel-pdp-upgrade__item'
    ).length;
    const text = (panel.textContent || '').replace(/\s+/g, '').length;
    return Boolean(media || items || text);
  };

  // The panels are ordinary child blocks; wire the tabpanel semantics here so
  // the merchant can use any block type as a tab.
  if (canWireTabs) {
    panels.forEach((panel, i) => {
      const tab = tabs[i];
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.setAttribute('data-ryx-pdp-panel', '');

      const panelId = tab.getAttribute('aria-controls');
      if (panelId) panel.id = panelId;
    });
  }

  const select = (index, moveFocus) => {
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      panels[i].hidden = !selected;
      if (selected) panels[i].removeAttribute('inert');
      else panels[i].setAttribute('inert', '');
    });

    if (moveFocus) tabs[index].focus();

    // Horizon carousels and lazy media inside a freshly revealed panel need a
    // resize pass once the panel has layout.
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  /**
   * Reconciles the shell with the panels that currently hold real content:
   *   none  → the whole shell disappears
   *   one   → that panel shows under its own label, without a tablist
   *   two + → the tablist shows, limited to panels that hold content
   * Runs again whenever a panel mutates, so asynchronously hydrated
   * recommendations never leave the shell hidden.
   */
  const syncPanels = () => {
    const withContent = panels
      .map((panel, index) => ({ panel, index }))
      .filter(({ panel }) => panelHasContent(panel));

    if (withContent.length === 0) {
      host.hidden = true;
      return;
    }

    host.hidden = false;

    if (withContent.length === 1 || !canWireTabs) {
      const survivor = withContent[0];

      panels.forEach((panel) => {
        const show = panel === survivor.panel;
        panel.hidden = !show;
        if (show) panel.removeAttribute('inert');
        else panel.setAttribute('inert', '');
      });

      if (nav) nav.hidden = true;

      // Keep the tabs in sync even while the nav is hidden, so a later change
      // (a panel gaining or losing content) cannot inherit a stale selection.
      if (canWireTabs) {
        tabs.forEach((tab, i) => {
          const isSurvivor = i === survivor.index;
          tab.hidden = !isSurvivor;
          tab.setAttribute('aria-selected', isSurvivor ? 'true' : 'false');
          tab.setAttribute('tabindex', isSurvivor ? '0' : '-1');
        });
      }

      if (singleLabel) {
        const labelText = (canWireTabs ? tabs[survivor.index]?.textContent : singleLabel.textContent) || '';
        const trimmed = labelText.trim();
        if (trimmed) singleLabel.textContent = trimmed;
        singleLabel.hidden = trimmed.length === 0;
      }
      return;
    }

    if (singleLabel) singleLabel.hidden = true;
    if (nav) nav.hidden = false;

    const visibleIndices = withContent.map(({ index }) => index);

    tabs.forEach((tab, i) => {
      const visible = visibleIndices.includes(i);
      tab.hidden = !visible;
      if (!visible) {
        tab.setAttribute('tabindex', '-1');
        tab.setAttribute('aria-selected', 'false');
      }
    });

    const activeIndex = visibleIndices.find((i) => tabs[i].getAttribute('aria-selected') === 'true');
    select(activeIndex === undefined ? visibleIndices[0] : activeIndex, false);
  };

  syncPanels();

  // Horizon hydrates recommendations after first paint; re-run when the panels
  // settle so a late-arriving panel gains its tab and an empty one loses it.
  if ('MutationObserver' in window) {
    let pending;
    const observer = new MutationObserver(() => {
      window.clearTimeout(pending);
      pending = window.setTimeout(syncPanels, 220);
    });
    observer.observe(panelsRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-has-recommendations', 'hidden', 'data-ryx-pdp-empty'] });
    host._ryxPdpTabsObserver = observer;
  }

  host.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-ryx-pdp-tab]');
    if (!tab || !host.contains(tab)) return;
    select(tabs.indexOf(tab), false);
  });

  host.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-ryx-pdp-tab]');
    if (!tab || !host.contains(tab)) return;

    const current = tabs.indexOf(tab);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    select(next, true);
  });

  const initial = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  select(initial < 0 ? 0 : initial, false);
}

function initTabs(root = document) {
  for (const host of root.querySelectorAll(TABS_SELECTOR)) enhanceTabs(host);
}

/* -------------------------------------------------------------------------
 * 3. Mobile media gallery — seamless loop + RYOXZEL arrows
 *
 * Horizon's slideshow already carries `infinite`, but its media gallery has no
 * visible edge neighbours on mobile: the first slide has nothing to its left and
 * no controls are shown. Rather than rebuild a carousel, this controller mirrors
 * the two boundary slides as non-interactive clones and scrolls Horizon's own
 * scroller, then snaps back to the matching real slide when a clone boundary is
 * crossed. Horizon keeps owning the slide index, media selection, variant sync
 * and zoom; the clones carry no `ref="slides[]"`, no ids and no focus targets,
 * so they never enter Horizon's state.
 * ---------------------------------------------------------------------- */

function enhanceMobileGallery(gallery) {
  if (!(gallery instanceof HTMLElement) || gallery.dataset.ryxGalleryReady === 'true') return;

  // The loop itself lives in `@ryoxzel/gallery-loop`, so the quick-add card can
  // run the same code at every width instead of a second copy of it.
  const teardown = enhanceGalleryLoop(gallery, { mediaQuery: MOBILE_MEDIA_QUERY });
  if (teardown) enhancedGalleries.add(gallery);
}

function initMobileGalleries(root = document) {
  for (const gallery of root.querySelectorAll(GALLERY_SELECTOR)) {
    // Horizon replaces or re-renders the gallery when the variant changes: a
    // fresh element is enhanced from scratch, an existing one is refreshed.
    if (gallery.dataset.ryxGalleryReady === 'true') gallery._ryxGalleryRefresh?.();
    else enhanceMobileGallery(gallery);
  }
}

/**
 * Horizon swaps the whole `<media-gallery>` element on variant changes, so the
 * media column is watched to re-enhance whatever gallery is live now.
 */
const MEDIA_CONTAINER_SELECTOR = `${PDP_SCOPE} .product-information__media`;

const cloneOnlyMutations = (mutations) =>
  mutations.every((mutation) =>
    [...mutation.addedNodes, ...mutation.removedNodes].every(
      (node) =>
        node.nodeType !== Node.ELEMENT_NODE ||
        node.hasAttribute?.('data-ryx-gallery-clone') ||
        node.closest?.('[data-ryx-gallery-clone]')
    )
  );

function watchMediaContainers(root = document) {
  for (const container of root.querySelectorAll(MEDIA_CONTAINER_SELECTOR)) {
    if (container.dataset.ryxGalleryWatched === 'true') continue;
    container.dataset.ryxGalleryWatched = 'true';

    const observer = new MutationObserver((mutations) => {
      if (cloneOnlyMutations(mutations)) return;

      // Horizon replaces the whole `<media-gallery>` element on a variant change,
      // and a fresh element starts at `scrollLeft: 0` — so it paints the first
      // image for as long as it takes us to re-enhance it. Measured on a 390px
      // viewport: the scroller sat at 0 for ~190ms before snapping back to the
      // selected slide, and that frame is the 0.040447 layout shift left on the
      // PDP.
      //
      // Enhancing here, inside the observer callback, puts the re-point in the
      // rAF that runs before the next paint, so the clamped state is never
      // shown. Only a gallery that is not enhanced yet is picked up now; the
      // debounced pass below still settles whatever the morph leaves behind.
      const fresh = Array.from(container.querySelectorAll(GALLERY_SELECTOR)).some(
        (gallery) => gallery.dataset.ryxGalleryReady !== 'true'
      );
      if (fresh) initMobileGalleries(container);

      window.clearTimeout(container._ryxGalleryTimer);
      container._ryxGalleryTimer = window.setTimeout(() => {
        for (const gallery of Array.from(enhancedGalleries)) {
          if (!gallery.isConnected) {
            gallery._ryxGalleryTeardown?.();
            enhancedGalleries.delete(gallery);
          }
        }
        initMobileGalleries(container);
      }, 160);
    });

    observer.observe(container, { childList: true, subtree: true });
    container._ryxGalleryWatcher = observer;
  }
}

/* -------------------------------------------------------------------------
 * 4. Option helper
 *
 * A second line inside Horizon's own option labels, so a bare "1.76 OZ" also
 * says what it means. Horizon keeps the group, the radios, availability and
 * the variant events; this controller only writes helper text, so nothing
 * about selection, pricing or the cart changes.
 * ---------------------------------------------------------------------- */

/** The leading number of an option value, e.g. `1.76 OZ` -> 1.76. */
function optionNumber(text) {
  const match = String(text ?? '').match(/-?\d+(?:[.,]\d+)?/);
  return match ? Number(match[0].replace(',', '.')) : Number.NaN;
}

/**
 * Horizon writes the group name as the legend's own text and, for swatch
 * groups, appends the selected value in a nested span. The span is dropped
 * before comparing so `ColorPink` still reads as `Color`.
 */
function optionGroupName(fieldset) {
  const legend = fieldset.querySelector('legend');
  if (!legend) return '';

  return Array.from(legend.childNodes)
    .filter(
      (node) =>
        node.nodeType !== Node.ELEMENT_NODE ||
        !node.classList?.contains('variant-option__swatch-value')
    )
    .map((node) => node.textContent ?? '')
    .join('')
    .trim();
}

function findOptionGroup(scope, groupName) {
  const wanted = String(groupName ?? '').trim().toLowerCase();
  if (!wanted) return null;

  for (const fieldset of scope.querySelectorAll(OPTION_GROUP_SELECTOR)) {
    if (optionGroupName(fieldset).toLowerCase() === wanted) return fieldset;
  }
  return null;
}

function buildHelperLine(mode) {
  const line = document.createElement('span');
  line.className = HELPER_LINE_CLASS;
  // Decorative: the option's own accessible name is unchanged.
  line.setAttribute('aria-hidden', 'true');

  if (mode === 'quantity_bar') {
    line.classList.add(`${HELPER_LINE_CLASS}--bar`);

    const bar = document.createElement('span');
    bar.className = 'ryoxzel-pdp-option-helper__bar';
    line.append(bar);

    // The bar answers "how much do I get"; the figure underneath answers "is
    // the larger size worth it". Both belong to a mix-in decoration, so the
    // same helper carries them and the merchant fills `values` only when they
    // want the second line.
    const value = document.createElement('span');
    value.className = 'ryoxzel-pdp-option-helper__bar-value';
    line.append(value);
  }

  return line;
}

/**
 * Marks one option as the recommended pick. Matched on the option's own text, so
 * a merchant can retype the value in the Theme Editor without breaking it, and
 * removed again when the setting changes or matches nothing.
 */
function applyOptionFlag(host, labels) {
  const highlight = (host.dataset.highlight ?? '').trim();
  const flagLabel = (host.dataset.flag ?? '').trim();

  for (const label of labels) {
    const text = (label.querySelector(OPTION_LABEL_TEXT_SELECTOR)?.textContent ?? '').trim();
    const existing = label.querySelector(`:scope > .${HELPER_FLAG_CLASS}`);

    if (!highlight || !flagLabel || text !== highlight) {
      existing?.remove();
      continue;
    }

    // Written only when it differs. The observer below re-runs this on every
    // picker mutation, so an unconditional write would replace a text node each
    // pass and keep the observer firing forever.
    if (existing) {
      if (existing.textContent !== flagLabel) existing.textContent = flagLabel;
      continue;
    }

    const flag = document.createElement('span');
    flag.className = HELPER_FLAG_CLASS;
    // Deliberately not aria-hidden: a recommendation is useful to a screen
    // reader, and it reads as part of the option's own accessible name.
    flag.textContent = flagLabel;
    label.append(flag);
  }
}

function enhanceOptionHelper(host) {
  if (!(host instanceof HTMLElement)) return;

  const scope = host.closest(PDP_SCOPE) ?? document;
  const mode = host.dataset.mode ?? 'squishy_count';
  const unitLabel = (host.dataset.label ?? '').trim();
  const values = String(host.dataset.values ?? '')
    .split(',')
    .map((value) => value.trim());

  const fieldset = findOptionGroup(scope, host.dataset.group);
  if (!fieldset) return;

  const labels = Array.from(fieldset.querySelectorAll(OPTION_LABEL_SELECTOR));
  if (labels.length === 0) return;

  // The bar is normalised against the group's own largest option, so the
  // smallest option reads as a fraction instead of an absolute size.
  let barMax = 0;
  if (mode === 'quantity_bar') {
    for (const label of labels) {
      const number = optionNumber(label.querySelector(OPTION_LABEL_TEXT_SELECTOR)?.textContent);
      if (Number.isFinite(number) && number > barMax) barMax = number;
    }
  }

  // Whether this group renders any helper at all. Read from the block's own data
  // rather than from the DOM: Horizon's morph removes the helper lines, so a DOM
  // check answers "no" during the gap and the pins below would be dropped
  // exactly when they are needed.
  const groupHasHelper =
    mode === 'quantity_bar' ? barMax > 0 : values.some((value) => Boolean(value));

  labels.forEach((label, index) => {
    const value = values[index];
    const hasText = mode === 'quantity_bar' ? barMax > 0 : Boolean(value);
    const existing = label.querySelector(`:scope > .${HELPER_LINE_CLASS}`);

    // A group whose options carry no helper keeps Horizon's own geometry.
    if (!hasText) {
      existing?.remove();
      return;
    }

    const line = existing ?? buildHelperLine(mode);
    if (!existing) label.append(line);

    // Every write below is guarded by an equality check. The observer re-runs
    // this whole function after each picker mutation, and an unconditional write
    // replaces a node every pass, which would keep the observer firing forever.
    if (mode === 'quantity_bar') {
      const number = optionNumber(label.querySelector(OPTION_LABEL_TEXT_SELECTOR)?.textContent);
      const ratio = Number.isFinite(number) ? (number / barMax) * 100 : 0;
      const nextBar = `${Math.round(ratio)}%`;

      const bar = line.querySelector('.ryoxzel-pdp-option-helper__bar');
      if (bar && bar.style.getPropertyValue('--ryx-option-bar') !== nextBar) {
        bar.style.setProperty('--ryx-option-bar', nextBar);
      }

      const figure = line.querySelector('.ryoxzel-pdp-option-helper__bar-value');
      const nextFigure = value ?? '';
      if (figure && figure.textContent !== nextFigure) figure.textContent = nextFigure;
      return;
    }

    const nextLine =
      mode === 'squishy_count' && unitLabel ? `≈ ${value} ${unitLabel}` : `≈ ${value}`;
    if (line.textContent !== nextLine) line.textContent = nextLine;
  });

  applyOptionFlag(host, labels);

  // Pin the tile height while the helper lines are actually present.
  //
  // Horizon's morph rebuilds the labels from server HTML on every variant
  // change, and that HTML never carries the helper lines, so the lines vanish
  // and the tile collapses until the observer below re-injects them. Measured
  // on the fillers template: the weight tile went 80px -> 42px and the picker
  // 324px -> 249px for about 128ms, which dragged the checklist and everything
  // under it up by 76px and back on every single click.
  //
  // The fieldset survives the morph, and so does its inline style, so the
  // measured height is written there and the CSS min-height keeps the tile at
  // its real size while the lines are briefly gone. Only measured when a line
  // exists, so a pass that runs inside the gap cannot pin the collapsed height.
  if (fieldset.querySelector(`.${HELPER_LINE_CLASS}`)) {
    const pinned = Math.round(labels[0].getBoundingClientRect().height);
    const next = `${pinned}px`;
    if (pinned > 0 && fieldset.style.getPropertyValue('--ryx-option-tile-min') !== next) {
      fieldset.style.setProperty('--ryx-option-tile-min', next);
    }
  }

  // Pin the mobile column width for the same reason, and against the same gap.
  //
  // The mobile rule narrows a helper group to two columns so the second line
  // stays legible. It used to be selected with
  // `:has(.ryoxzel-pdp-option-helper__line)`, and the morph removes those lines —
  // so mid-morph the selector stopped matching, the grid snapped back to
  // Horizon's column count and every tile reflowed before the lines returned.
  // Measured on the decoration-kit template at 390px: labels went from 170px
  // wide to 110px and swapped grid cells, twice on every variant change.
  //
  // Written to the fieldset's inline style, which survives the morph that removes
  // the lines it describes, and read by the mobile rule in
  // `assets/ryoxzel-pdp.css` with Horizon's own `--variant-min-width` as the
  // fallback — so a group without helpers gets Horizon's track list untouched.
  const COLUMN_MIN_WIDTH = '46%';
  const pinnedColumn = fieldset.style.getPropertyValue('--ryx-option-min-width');
  if (groupHasHelper) {
    if (pinnedColumn !== COLUMN_MIN_WIDTH) {
      fieldset.style.setProperty('--ryx-option-min-width', COLUMN_MIN_WIDTH);
    }
  } else if (pinnedColumn) {
    fieldset.style.removeProperty('--ryx-option-min-width');
  }
}

/**
 * Horizon re-renders the picker on a variant change by morphing it in place, and
 * the server HTML never carries the helper lines, so the morph removes them. The
 * picker element itself survives, which is why it is watched instead of the
 * labels — but the removal is the one mutation that has to trigger a re-run.
 *
 * An earlier version filtered out mutations that only touched helper nodes, to
 * stop the observer reacting to its own injections. That also swallowed the
 * morph's removal of them, so the helpers never came back after the first
 * variant change and the whole group fell back to Horizon's default geometry.
 * The filter is gone; loop safety now comes from every write in
 * `enhanceOptionHelper` and `applyOptionFlag` being a no-op when the value
 * already matches, so a re-run settles instead of re-triggering.
 */
function initOptionHelpers(root = document) {
  for (const host of root.querySelectorAll(OPTION_HELPER_SELECTOR)) {
    enhanceOptionHelper(host);

    const picker = host.closest(PDP_SCOPE)?.querySelector(OPTION_PICKER_SELECTOR);
    if (!picker || picker.dataset.ryxOptionHelperWatched === 'true') continue;
    picker.dataset.ryxOptionHelperWatched = 'true';

    // Coalesced to the next frame instead of a 120ms timer. With the tile
    // height pinned the layout no longer moves, but the timer was still the
    // visible half of the gap: the second line stayed blank for the whole
    // debounce. One frame is below the threshold where the reappearance reads
    // as a flicker. Loop safety is unchanged — every write in
    // `enhanceOptionHelper` and `applyOptionFlag` is a no-op when the value
    // already matches, so a re-run settles instead of re-triggering.
    const observer = new MutationObserver(() => {
      if (picker._ryxOptionHelperFrame) return;
      picker._ryxOptionHelperFrame = window.requestAnimationFrame(() => {
        picker._ryxOptionHelperFrame = 0;
        initOptionHelpers(picker.closest(PDP_SCOPE) ?? document);
      });
    });

    observer.observe(picker, { childList: true, subtree: true });
    picker._ryxOptionHelperObserver = observer;
  }
}

/* -------------------------------------------------------------------------
 * Review disclosures
 *
 * Judge.me clamps a long review to five lines with an inline
 * `-webkit-line-clamp` and renders its own "More" control. Clicking it expands
 * the copy and then REMOVES the control, so the review can never be collapsed
 * again and the change is instant with no motion.
 *
 * This replaces that control with one that owns both directions and animates
 * the way the PDP accordions do. The collapsed box is measured before the clamp
 * is swapped for a max-height, because a line clamp cannot be transitioned —
 * the cost is that the collapsed state ends on a clean line edge instead of a
 * text-overflow ellipsis.
 *
 * Judge.me renders the widget asynchronously and re-renders it on sort and
 * filter, so this runs on an observer as well as on load.
 * ---------------------------------------------------------------------- */
const REVIEW_BODY_SELECTOR = '.ryoxzel-pdp-reviews .jm-review-content__body--clickable';
const REVIEW_MORE_SELECTOR = '.ryoxzel-pdp-reviews .jm-review-content__expand-button';
const REVIEW_TOGGLE_CLASS = 'ryoxzel-reviews-toggle';
const REVIEW_TOGGLE_DURATION = 320;

function initReviewDisclosures(root = document) {
  for (const body of root.querySelectorAll(REVIEW_BODY_SELECTOR)) {
    if (body.dataset.ryxReviewsReady === 'true') continue;

    const container = body.closest('.jm-review-content');
    const judgeMore = container?.querySelector(REVIEW_MORE_SELECTOR);
    // Judge.me only renders its control when the copy actually overflows, so
    // its presence is the signal that this review needs a toggle at all.
    if (!judgeMore || !judgeMore.parentElement) continue;

    body.dataset.ryxReviewsReady = 'true';

    const collapsed = Math.round(body.getBoundingClientRect().height);
    if (collapsed <= 0) continue;

    body.style.removeProperty('-webkit-line-clamp');
    body.style.removeProperty('-webkit-box-orient');
    body.style.display = 'block';
    body.style.overflow = 'hidden';
    body.style.maxHeight = `${collapsed}px`;
    if (!prefersReducedMotion()) {
      body.style.transition = `max-height ${REVIEW_TOGGLE_DURATION}ms var(--ease-out-cubic, ease-out)`;
    }

    judgeMore.hidden = true;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = REVIEW_TOGGLE_CLASS;
    toggle.textContent = 'Read more';
    toggle.setAttribute('aria-expanded', 'false');
    judgeMore.parentElement.insertBefore(toggle, judgeMore);

    toggle.addEventListener('click', () => {
      const open = body.dataset.ryxReviewsOpen === 'true';
      body.dataset.ryxReviewsOpen = open ? 'false' : 'true';
      body.style.maxHeight = open ? `${collapsed}px` : `${body.scrollHeight}px`;
      toggle.textContent = open ? 'Read more' : 'Show less';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }
}

/* Judge.me renders "Write a review" inside its own summary band, where it sits
   at the far right of the distribution column — measured 1170px from the
   heading it belongs to, on the far side of the band. The section header is the
   RYOXZEL surface, so the control is moved up beside the heading instead.

   It is moved, not cloned: the button is a Vue-rendered node and a clone would
   lose its click handler. Judge.me keeps the element in its own vnode tree, so
   a re-render on sort or filter can put it back — the observer below re-runs
   this and moves it again, and the run that finds it already in the header does
   nothing, so the loop terminates.

   Only the populated band's control is moved. The empty state has its own
   button inside `.jm-no-reviews-state__action-buttons`, which belongs to that
   card and stays there. */
const REVIEW_CTA_SELECTOR = '.jm-review-widget-header__main-content > .jm-action-buttons';
const REVIEW_HEADER_SELECTOR = '.ryoxzel-pdp-reviews__header';

function initReviewWriteCta(root = document) {
  // `root` is either the document (boot) or one section (the observer below),
  // and an element is not its own descendant — so the section has to be taken
  // directly in the second case or the loop never runs. Measured: called with
  // the section, `section.querySelectorAll('.ryoxzel-pdp-reviews')` matched
  // nothing and the control was never moved.
  const sections = root.matches?.('.ryoxzel-pdp-reviews')
    ? [root]
    : Array.from(root.querySelectorAll('.ryoxzel-pdp-reviews'));

  for (const section of sections) {
    const header = section.querySelector(REVIEW_HEADER_SELECTOR);
    if (!header) continue;

    // Absent until Judge.me mounts, and re-created when it re-renders.
    const cta = section.querySelector(REVIEW_CTA_SELECTOR);
    if (!cta || cta.parentElement === header) continue;

    header.appendChild(cta);
  }
}

/* Judge.me concatenates its rating's accessible text into the review title as
   plain text. Measured on the live theme: the element's own `innerHTML` reads
   `5 out of 5 starsFun and Creative Stress Ball Kit With Tons of Fillers and
   Options`, with no wrapper element around the rating phrase — so there is
   nothing a stylesheet could hide, and the text has to be removed.

   The match is deliberately narrow. It only strips a leading rating phrase, it
   only touches a title that is a single text node (so a title that ever gains
   markup is left alone), and a title that does not match is left as it is. It
   depends on Judge.me's English wording: if that changes, the phrase simply
   stays visible again rather than anything being mangled. */
const REVIEW_TITLE_SELECTOR = '.ryoxzel-pdp-reviews .jm-review-content__title';
const REVIEW_RATING_PREFIX = /^\s*\d+(?:\.\d+)?\s+out of\s+5\s+stars\s*/i;

function initReviewTitles(root = document) {
  // An element is not its own descendant, so the section has to be taken
  // directly when the observer passes one in.
  const sections = root.matches?.('.ryoxzel-pdp-reviews')
    ? [root]
    : Array.from(root.querySelectorAll('.ryoxzel-pdp-reviews'));

  for (const section of sections) {
    for (const title of section.querySelectorAll(REVIEW_TITLE_SELECTOR)) {
      if (title.childElementCount > 0) continue;

      const text = title.textContent ?? '';
      if (!REVIEW_RATING_PREFIX.test(text)) continue;

      const cleaned = text.replace(REVIEW_RATING_PREFIX, '').trim();
      if (cleaned) title.textContent = cleaned;
    }
  }
}

/* The filter panel opens the way the PDP accordions do: a height and opacity
   motion on the shared easing, with the duration scaled to the distance it has
   to travel — `clamp(280ms, height / 0.22, 480ms)`, the contract the PDP
   accordions already use, and nothing at all under reduced motion.

   Judge.me inserts the panel on the first click and removes it on the next, so
   only the opening is ours to animate; the removal is the widget's and happens
   without a transition. */
const FILTER_PANEL_SELECTOR = '.ryoxzel-pdp-reviews .jm-filter-panel';

function filterPanelDuration(height) {
  return Math.min(Math.max(280, height / 0.22), 480);
}

function initFilterPanelMotion(section) {
  const panel = section.querySelector(FILTER_PANEL_SELECTOR);
  if (!panel || panel.dataset.ryxPanelAnimated === 'true') return;
  panel.dataset.ryxPanelAnimated = 'true';
  if (prefersReducedMotion()) return;

  const height = Math.round(panel.getBoundingClientRect().height);
  if (height <= 0) return;

  const duration = filterPanelDuration(height);

  panel.style.overflow = 'hidden';
  panel.style.height = '0px';
  panel.style.opacity = '0';

  // Read the start state back before writing the target: without this the
  // browser has no "from" value and the transition never runs.
  void panel.offsetHeight;

  panel.style.transition = `height ${duration}ms var(--ease-out-cubic, ease-out), opacity ${duration}ms var(--ease-out-cubic, ease-out)`;
  panel.style.height = `${height}px`;
  panel.style.opacity = '1';

  panel.addEventListener(
    'transitionend',
    () => {
      panel.style.removeProperty('height');
      panel.style.removeProperty('overflow');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('transition');
    },
    { once: true }
  );
}

/* Judge.me mounts its widget after load and re-renders it on sort and filter,
   so the disclosures above are re-checked whenever the band changes. The
   observer watches childList only — the toggle writes inline style, and
   watching attributes would make it retrigger itself. */
function watchReviewDisclosures(root = document) {
  for (const section of root.querySelectorAll('.ryoxzel-pdp-reviews')) {
    if (section.dataset.ryxReviewsWatched === 'true') continue;
    section.dataset.ryxReviewsWatched = 'true';

    const observer = new MutationObserver(() => {
      if (section._ryxReviewsFrame) return;
      section._ryxReviewsFrame = window.requestAnimationFrame(() => {
        section._ryxReviewsFrame = 0;
        initReviewDisclosures(section);
        initReviewWriteCta(section);
        initReviewTitles(section);
        initFilterPanelMotion(section);
      });
    });

    observer.observe(section, { childList: true, subtree: true });
    section._ryxReviewsObserver = observer;
  }
}

/* -------------------------------------------------------------------------
 * Boot + Theme Editor lifecycle
 * ---------------------------------------------------------------------- */

function initPdp(root = document) {
  initAccordions(root);
  initDisclosures(root);
  initTabs(root);
  initMobileGalleries(root);
  initOptionHelpers(root);
  initQuantityTiers(root);
  watchMediaContainers(root);
  initReviewDisclosures(root);
  initReviewWriteCta(root);
  initReviewTitles(root);
  watchReviewDisclosures(root);
}

if (!prefersReducedMotion()) {
  initPdp();
} else {
  // Reduced motion still needs the semantic tabs wiring; only the accordion
  // motion tuning is skipped, because Horizon renders it instantly anyway.
  initTabs();
  initMobileGalleries();
  initOptionHelpers();
  initQuantityTiers();
  // None of the review tasks animate, and all three depend on the observer:
  // Judge.me mounts its widget after load, so a one-shot call at boot finds
  // nothing to move or clean up. The disclosure toggle writes inline style only
  // when it is clicked, so it stays inert here.
  initReviewWriteCta();
  initReviewTitles();
  watchReviewDisclosures();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  initPdp(target instanceof HTMLElement ? target : document);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  teardownDisclosures(target);

  for (const gallery of target.querySelectorAll(GALLERY_SELECTOR)) {
    gallery._ryxGalleryTeardown?.();
    delete gallery.dataset.ryxGalleryReady;
  }

  for (const host of target.querySelectorAll(TABS_SELECTOR)) {
    if (host._ryxPdpTabsObserver) {
      host._ryxPdpTabsObserver.disconnect();
      host._ryxPdpTabsObserver = null;
    }
    if (host.dataset.ryxPdpTabsReady === 'true') host.dataset.ryxPdpTabsReady = 'false';
  }

  for (const picker of target.querySelectorAll(OPTION_PICKER_SELECTOR)) {
    if (picker._ryxOptionHelperObserver) {
      picker._ryxOptionHelperObserver.disconnect();
      picker._ryxOptionHelperObserver = null;
    }
    if (picker._ryxOptionHelperFrame) {
      window.cancelAnimationFrame(picker._ryxOptionHelperFrame);
      picker._ryxOptionHelperFrame = 0;
    }
    if (picker.dataset.ryxOptionHelperWatched === 'true') {
      picker.dataset.ryxOptionHelperWatched = 'false';
    }
  }

  // The editor removes a section by unloading it, so the tier markers have to go
  // with it or Horizon's picker stays hidden on the next render.
  teardownQuantityTiers(target);
});
